import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { getURL } from "core-integration/src/store/urls/reducers";
import { fetchURL } from "core-integration/src/store/urls/thunks";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Parser from "rss-parser";
import styled from "styled-components";
import { getPlaymakerNewsParams } from "../../utils/playmaker";
import { ControlArrowRight } from "../icons/Arrows";
import filterOnSSL from "./filterOnSSL";

const parser = new Parser();

const StyledProvider = styled.section`
  border: ${({ theme }) => theme.borders[3]};
  margin-bottom: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const ProviderHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space[2]};
  border-top-right-radius: ${({ theme }) => theme.radii[2]};
  border-top-left-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const ProviderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.space[2]};
  margin: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProviderFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-top: ${({ theme }) => theme.borders[3]};
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
  gap: 0;
`;

const Article = styled.article`
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.radii[2]};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ArticleTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[3]};
`;

const StyledMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes[1]};
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  text-transform: uppercase;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const SubHeading = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: 1.6rem;
  font-weight: bold;
  text-transform: uppercase;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    font-size: 2rem;
  }
`;

interface IProps {
  link: string;
  url: string;
  title: string;
  numberOfArticles: number;
}

const ScoutProvider: React.FC<IProps> = ({
  link,
  url,
  numberOfArticles,
  title,
}) => {
  const { t } = useTranslation();
  const player = useSelector(getPlayerData);
  const [providerData, setProviderData] = useState<Parser.Item[] | null>(null);

  const moreUrlPath = getPlaymakerNewsParams(player, "news_see_all");
  const articleUrlPath = getPlaymakerNewsParams(player, "news_item");

  // Fetch RSS
  const reduxDispatch = useDispatch<ThunkDispatch>();
  useEffect(() => {
    reduxDispatch(fetchURL(url));
  }, [reduxDispatch, url]);

  // Get raw RSS content, will be null until we have some
  const content = useSelector((state: RootState) => getURL(state, url));

  // Parse RSS data
  useEffect(() => {
    const parseContent = async () => {
      try {
        const feed = await parser.parseString(content.text);
        if (feed && feed.items) {
          const filteredSslFeedItems = filterOnSSL(feed.items).splice(
            0,
            numberOfArticles,
          );

          setProviderData(filteredSslFeedItems);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (content) {
      parseContent();
    }
  }, [content, numberOfArticles]);

  if (!providerData) {
    return null;
  }

  return (
    <StyledProvider style={{ marginBottom: 20 }}>
      <ProviderHeader>
        <SubHeading>{title}</SubHeading>
      </ProviderHeader>
      <ProviderContent>
        {providerData.map((item) => {
          return (
            <Article key={item.title}>
              <StyledLink
                href={`${item.link}/?${articleUrlPath}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArticleTitle>{item.title}</ArticleTitle>
                {item.contentSnippet && <p>{item.contentSnippet}</p>}
              </StyledLink>
            </Article>
          );
        })}
      </ProviderContent>
      <ProviderFooter>
        <StyledMoreLink
          href={`${link}/?${moreUrlPath}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("scout.more", "More")}
          <ControlArrowRight />
        </StyledMoreLink>
      </ProviderFooter>
    </StyledProvider>
  );
};

export default ScoutProvider;
